import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "antd";
import { AppDispatch, RootState } from "../../app/store";
import { useSearchParams } from "react-router-dom";
import {
  fetchCategories,
  fetchProductPortfolio,
  productsPortfolioActions,
  fetchProductDetails,
  ProductPortfolioDetails,
  exportProductsToCSV,
} from "../../modules/productsPortfolio";
import {
  ProductsPortfolioList,
  ProductsPortfolioFilters,
  IProductFormValues,
  CreateProductForm,
} from "../../modules/productsPortfolio";
import { FormModal } from "../../components/FormModal";
import { format } from "date-fns";
import styles from "./ProductsPortfolio.module.css";

export const ProductsPortfolio = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<IProductFormValues>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("productsPortfolio");

  const { status, list, categories, selectedProduct, filters, labels } =
    useSelector((state: RootState) => {
      return state.productsPortfolio;
    });

  const metadataEntries = useSelector((state: RootState) => {
    return state.metadata.list;
  });

  const getProductsPortfolio = useCallback(() => {
    dispatch(fetchProductPortfolio(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(
      productsPortfolioActions.setFilters({
        searchString: searchParams.get("searchString") || "",
        categories: searchParams.get("categories") || "",
        disabledSystems: searchParams.get("disabledSystems") === "true",
        status: searchParams.get("status") || "",
      })
    );
  }, [searchParams, dispatch]);

  const getProductsCategories = useCallback(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    getProductsPortfolio();
    getProductsCategories();
  }, [getProductsPortfolio, getProductsCategories]);

  useEffect(() => {
    const productId = searchParams.get("productId");
    if (!productId) return;
    if (selectedProduct && selectedProduct.id === Number(productId)) return;
    dispatch(fetchProductDetails(Number(productId)));
  }, [searchParams, dispatch, selectedProduct]);

  const showCreateProductModal = () => setCreateProductModalOpen(true);
  const hideCreateProductModal = () => setCreateProductModalOpen(false);

  const hideDetailsDrawer = () => {
    searchParams.delete("productId");
    setSearchParams(searchParams);
    dispatch(productsPortfolioActions.clearSelectedProduct());
  };

  const getProductDetails = (productId: number) => {
    searchParams.set("productId", productId.toString());
    setSearchParams(searchParams);
    dispatch(fetchProductDetails(productId));
  };

  const handleConfirmCreateProduct = () => form.submit();

  const handleExportProducts = useCallback(async () => {
    const csv = await exportProductsToCSV(filters);
    const name = `products_portfolio_${format(new Date(), "dd-mm-yyyy_HH:MM")}`;
    const blob = new Blob([csv.data], { type: "text/csv;charset=utf-8" });
    saveAs(blob, name);
  }, [filters]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <ProductsPortfolioFilters
          filters={filters}
          categories={categories}
          onShowCreateProductModal={showCreateProductModal}
          onExport={handleExportProducts}
        />
      </div>
      <div className={styles.list}>
        <ProductsPortfolioList
          list={list}
          status={status}
          onSelectProduct={getProductDetails}
          filters={filters}
          categories={categories}
        />
      </div>
      <FormModal
        onConfirm={handleConfirmCreateProduct}
        submitText={t("create")}
        loading={loading}
        content={
          <CreateProductForm
            form={form}
            onClose={hideCreateProductModal}
            categories={categories}
            labels={labels}
            metadataEntries={metadataEntries}
            onChangeLoading={setLoading}
          />
        }
        open={createProductModalOpen}
        onCancel={hideCreateProductModal}
      />
      <ProductPortfolioDetails
        product={selectedProduct}
        categories={categories}
        onHide={hideDetailsDrawer}
        metadataEntries={metadataEntries}
      />
    </div>
  );
};
